import React from "react";
import { LogOut, Menu, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { initials } from "@/lib/format";
import { NotificationBell } from "@/components/layout/NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  tenant_admin: "Tenant Admin",
  manager: "Manager",
  buyer: "Buyer",
};

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Light theme" : "Dark theme"}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.1] bg-white/[0.02] text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground"
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  if (!user) return null;

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-white/[0.08] bg-[#0A0A0A]/80 px-4 backdrop-blur-md sm:px-6">
      <button onClick={onMenuClick} data-testid="mobile-menu-btn" aria-label="Open menu"
        className="-ml-1 rounded-lg p-2 text-muted-foreground hover:bg-white/[0.05] hover:text-foreground lg:hidden">
        <Menu className="h-5 w-5" />
      </button>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
        {/* Theme toggle */}
        <ThemeToggle />

        {/* User menu */}
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu" className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 transition-colors hover:bg-white/[0.04]">
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white/[0.08] text-xs font-medium text-foreground">
                {user.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : initials(user.name)}
              </div>
              <div className="hidden text-left leading-tight sm:block">
                <div className="text-sm font-medium text-foreground">{user.name}</div>
                <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[user.role]}{user.tenant_name ? ` · ${user.tenant_name}` : ""}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 border-white/[0.1] bg-[#1a1a1e]">
            <DropdownMenuLabel>
              <div className="text-sm text-foreground">{user.name}</div>
              <div className="text-xs font-normal text-muted-foreground">{user.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/[0.08]" />
            <DropdownMenuItem
              onClick={logout}
              data-testid="logout-btn"
              className="cursor-pointer gap-2 text-[#EF4444] focus:text-[#EF4444]"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
